import React, { createContext, useState, useContext, useEffect } from 'react';

const FullscreenContext = createContext();

export const useFullscreen = () => {
  const context = useContext(FullscreenContext);
  if (!context) {
    throw new Error('useFullscreen must be used within a FullscreenProvider');
  }
  return context;
};

export const FullscreenProvider = ({ children }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if running on iOS
  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    // iOS doesn't support fullscreen API, show a message or handle differently
    if (isIOS()) {
      // On iOS, we can't do true fullscreen, but we can suggest adding to home screen
      // or just toggle UI elements to maximize viewport
      console.log('Fullscreen not supported on iOS devices');
      // You could emit a custom event here to hide UI elements
      window.dispatchEvent(
        new CustomEvent('toggle-ios-fullscreen', {
          detail: { isFullscreen: !isFullscreen },
        })
      );
      setIsFullscreen(!isFullscreen);
      return;
    }

    // Standard fullscreen API for other browsers
    //
    // Every one of these returns a promise that legitimately REJECTS: the user
    // dismissed the permission, the call did not come from a trusted gesture,
    // or the embedder disallows fullscreen. Unhandled, that surfaces as
    // `Uncaught (in promise) TypeError` — which in dev pops CRA's full-screen
    // red error overlay over the app for what is a normal "no".
    const ignoreRefusal = (result) => {
      if (result && typeof result.catch === 'function') {
        result.catch((err) => {
          console.warn('[Fullscreen] request refused:', err?.message || err);
        });
      }
    };

    if (
      !document.fullscreenElement &&
      !document.webkitFullscreenElement &&
      !document.mozFullScreenElement &&
      !document.msFullscreenElement
    ) {
      // Enter fullscreen
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        ignoreRefusal(elem.requestFullscreen());
      } else if (elem.webkitRequestFullscreen) {
        ignoreRefusal(elem.webkitRequestFullscreen());
      } else if (elem.mozRequestFullScreen) {
        ignoreRefusal(elem.mozRequestFullScreen());
      } else if (elem.msRequestFullscreen) {
        ignoreRefusal(elem.msRequestFullscreen());
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        ignoreRefusal(document.exitFullscreen());
      } else if (document.webkitExitFullscreen) {
        ignoreRefusal(document.webkitExitFullscreen());
      } else if (document.mozCancelFullScreen) {
        ignoreRefusal(document.mozCancelFullScreen());
      } else if (document.msExitFullscreen) {
        ignoreRefusal(document.msExitFullscreen());
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isInFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Check initial state
    setIsFullscreen(!!document.fullscreenElement);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange
      );
    };
  }, []);

  return (
    <FullscreenContext.Provider value={{ isFullscreen, toggleFullscreen }}>
      {children}
    </FullscreenContext.Provider>
  );
};
